import { useCallback, useEffect, useMemo, useState } from "react";
import {
  type Api,
  ApiError,
  type StaffCustomerDetail,
  type StaffCustomerSummary,
  type StaffOverrides,
} from "../api";

/**
 * Founder back-office (M1): the customer list + entitlement-overrides
 * editor. Overrides are sparse — "default" means the plan tier decides;
 * an override pins the flag/limit regardless of tier. Saving re-projects
 * the serve-time KV config immediately (canonical render-config path).
 */
export function StaffPanel({ api }: { api: Api }) {
  const [customers, setCustomers] = useState<StaffCustomerSummary[] | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [sweepBusy, setSweepBusy] = useState(false);
  const [sweepResult, setSweepResult] = useState<string | null>(null);

  const sweep = async () => {
    if (sweepBusy) return;
    setSweepBusy(true);
    setSweepResult(null);
    try {
      const result = await api.staffRenderConfigSweep();
      setSweepResult(
        `Swept ${result.tenants} tenant(s): ${result.refreshedHosts} host config(s) refreshed, ${result.unchangedHosts} already converged${
          result.errors.length > 0
            ? `, ${result.errors.length} error(s): ${result.errors
                .map((e) => e.tenant)
                .join(", ")}`
            : ""
        }.`,
      );
    } catch (caught) {
      setSweepResult(
        caught instanceof ApiError ? caught.message : "Sweep failed.",
      );
    } finally {
      setSweepBusy(false);
    }
  };

  const load = useCallback(async () => {
    try {
      setCustomers((await api.staffCustomers()).customers);
      setError(null);
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Could not load customers.",
      );
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section className="card staff-card">
      <div className="card-head">
        <div>
          <h2>Customers</h2>
          <p className="muted">
            Plans, billing state, and per-customer entitlement overrides.
            Overrides win over the plan tier and take effect on the live site
            immediately.
          </p>
        </div>
        <button
          className="btn-secondary"
          data-testid="staff-render-config-sweep"
          disabled={sweepBusy}
          onClick={sweep}
          title="Re-project every provisioned tenant's serve-time config — run after editing tier defaults"
          type="button"
        >
          {sweepBusy ? "Sweeping…" : "Re-project all tenants"}
        </button>
      </div>
      {sweepResult && <p className="muted small">{sweepResult}</p>}
      {error && <p className="muted small">{error}</p>}
      {!error && customers === null && <p className="muted small">Loading…</p>}
      {customers !== null && customers.length === 0 && (
        <p className="muted small">No customers yet.</p>
      )}
      {customers !== null && customers.length > 0 && (
        <ul className="staff-list">
          {customers.map((customer) => {
            const overrideCount =
              Object.keys(customer.overrides.features).length +
              Object.keys(customer.overrides.limits).length;
            return (
              <li key={customer.id}>
                <button
                  type="button"
                  className={`staff-row${selectedId === customer.id ? " active" : ""}`}
                  onClick={() =>
                    setSelectedId((current) =>
                      current === customer.id ? null : customer.id,
                    )
                  }
                >
                  <span className="staff-row-name">
                    {customer.businessName ?? customer.email}
                  </span>
                  <span className="muted small">
                    {customer.customDomain ?? customer.hostname ?? "no site"}
                  </span>
                  <span className="plan-pill">{customer.plan}</span>
                  <span className="muted small">{customer.billingState}</span>
                  {overrideCount > 0 && (
                    <span className="staff-override-count">
                      {overrideCount} override{overrideCount === 1 ? "" : "s"}
                    </span>
                  )}
                </button>
                {selectedId === customer.id && (
                  <OverridesEditor
                    api={api}
                    customerId={customer.id}
                    onSaved={load}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

type FeatureChoice = "default" | "on" | "off";

function OverridesEditor({
  api,
  customerId,
  onSaved,
}: {
  api: Api;
  customerId: string;
  onSaved: () => void;
}) {
  const [detail, setDetail] = useState<StaffCustomerDetail | null>(null);
  const [features, setFeatures] = useState<Record<string, FeatureChoice>>({});
  const [limits, setLimits] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedNote, setSavedNote] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const next = await api.staffCustomer(customerId);
        if (cancelled) return;
        setDetail(next);
        const featureDraft: Record<string, FeatureChoice> = {};
        for (const [key, value] of Object.entries(next.overrides.features)) {
          featureDraft[key] = value ? "on" : "off";
        }
        setFeatures(featureDraft);
        const limitDraft: Record<string, string> = {};
        for (const [key, value] of Object.entries(next.overrides.limits)) {
          limitDraft[key] = value === null ? "unlimited" : String(value);
        }
        setLimits(limitDraft);
      } catch (caught) {
        if (cancelled) return;
        setError(
          caught instanceof ApiError ? caught.message : "Could not load.",
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, customerId]);

  const featureKeys = useMemo(
    () => (detail ? Object.keys(detail.effective.features).sort() : []),
    [detail],
  );
  const limitKeys = useMemo(
    () => (detail ? Object.keys(detail.effective.limits).sort() : []),
    [detail],
  );

  const save = async () => {
    if (busy) return;
    const overrides: StaffOverrides = { features: {}, limits: {} };
    for (const [key, choice] of Object.entries(features)) {
      if (choice !== "default") overrides.features[key] = choice === "on";
    }
    for (const [key, raw] of Object.entries(limits)) {
      const text = raw.trim().toLowerCase();
      if (text === "") continue;
      if (text === "unlimited" || text === "∞") {
        overrides.limits[key] = null;
        continue;
      }
      const value = Number(text);
      if (!Number.isInteger(value) || value < 0) {
        setError(
          `"${raw}" is not a valid ${key} — use a whole number, "unlimited", or leave blank.`,
        );
        return;
      }
      overrides.limits[key] = value;
    }
    setBusy(true);
    setError(null);
    setSavedNote(null);
    try {
      const result = await api.staffSetOverrides(customerId, overrides);
      setDetail((current) =>
        current
          ? {
              ...current,
              overrides: result.overrides,
              effective: result.effective,
            }
          : current,
      );
      setSavedNote(
        result.renderConfig.refreshed.length > 0
          ? `Saved. Live site config refreshed: ${result.renderConfig.refreshed.join(", ")}`
          : result.renderConfig.unchanged.length > 0
            ? "Saved. Live site config was already up to date."
            : "Saved. No published site config to refresh yet.",
      );
      onSaved();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not save.");
    } finally {
      setBusy(false);
    }
  };

  if (error && !detail) return <p className="muted small">{error}</p>;
  if (!detail) return <p className="muted small">Loading…</p>;

  return (
    <div className="staff-editor">
      <p className="muted small">
        {detail.customer.email}
        {detail.customer.country ? ` · ${detail.customer.country}` : ""} ·
        signed up {detail.customer.createdAt.slice(0, 10)}
      </p>
      <div className="staff-editor-grid">
        <div>
          <h3>Features</h3>
          {featureKeys.map((key) => (
            <label key={key} className="staff-field">
              <span className="staff-field-name">
                {key}
                <span
                  className={`staff-effective${detail.effective.features[key] ? " on" : ""}`}
                >
                  {detail.effective.features[key] ? "on" : "off"}
                </span>
              </span>
              <select
                value={features[key] ?? "default"}
                onChange={(event) =>
                  setFeatures((current) => ({
                    ...current,
                    [key]: event.target.value as FeatureChoice,
                  }))
                }
              >
                <option value="default">plan default</option>
                <option value="on">always on</option>
                <option value="off">always off</option>
              </select>
            </label>
          ))}
        </div>
        <div>
          <h3>Limits</h3>
          {limitKeys.map((key) => {
            const effective = detail.effective.limits[key];
            return (
              <label key={key} className="staff-field">
                <span className="staff-field-name">
                  {key}
                  <span className="staff-effective">
                    {effective === null ? "∞" : String(effective)}
                  </span>
                </span>
                <input
                  type="text"
                  placeholder="plan default"
                  value={limits[key] ?? ""}
                  onChange={(event) =>
                    setLimits((current) => ({
                      ...current,
                      [key]: event.target.value,
                    }))
                  }
                />
              </label>
            );
          })}
        </div>
      </div>
      <div className="staff-editor-actions">
        <button
          type="button"
          className="btn-secondary"
          onClick={save}
          disabled={busy}
        >
          {busy ? "Saving…" : "Save overrides"}
        </button>
        {savedNote && <span className="muted small">{savedNote}</span>}
        {error && <span className="muted small">{error}</span>}
      </div>
      <p className="muted small">
        Effective values (shown next to each name) update after save. Limit
        overrides: whole number, “unlimited”, or blank for the plan default.
      </p>
    </div>
  );
}
